<template>
  <div class="p-6">
    <h2 class="text-xl font-bold mb-4">จัดการรางวัล</h2>

    <!-- ฟอร์มสำหรับเพิ่มรางวัลใหม่ -->
    <div class="mb-6 border p-4 rounded shadow">
      <h3 class="text-lg font-semibold mb-2">เพิ่มรางวัลใหม่</h3>
      <form @submit.prevent="addReward" class="flex flex-col space-y-4">
        <div>
          <label class="block mb-1">ชื่อรางวัล</label>
          <input
            v-model="newRewardLabel"
            type="text"
            placeholder="ชื่อรางวัล"
            class="w-full border px-3 py-2 rounded"
          />
        </div>
        <div>
          <label class="block mb-1">จำนวนเงิน</label>
          <input
            v-model.number="newRewardAmount"
            type="number"
            placeholder="จำนวน"
            class="w-full border px-3 py-2 rounded"
          />
        </div>
        <button type="submit" class="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700">
          เพิ่มรางวัล
        </button>
      </form>
    </div>

    <!-- ตารางแสดงรายการรางวัล -->
    <table class="w-full table-auto border rounded shadow text-left">
      <thead class="bg-gray-200">
        <tr>
          <th class="px-4 py-2">#</th>
          <th class="px-4 py-2">ชื่อรางวัล</th>
          <th class="px-4 py-2">จำนวนเงิน</th>
          <th class="px-4 py-2">การกระทำ</th>
        </tr>
      </thead>
      <tbody>
        <!-- ถ้าไม่มีข้อมูล -->
        <tr v-if="!rewards.length">
          <td class="px-4 py-2" colspan="4">ไม่พบข้อมูล</td>
        </tr>
        <!-- วนลูปรายการรางวัล -->
        <tr v-for="(reward, index) in rewards" :key="reward._id" class="border-b hover:bg-gray-50">
          <td class="px-4 py-2">{{ index + 1 }}</td>
          <td class="px-4 py-2">
            <!-- หากกำลังแก้ไขรายการนี้ ให้แสดง input -->
            <div v-if="editingRewardId === reward._id">
              <input
                v-model="editingRewardLabel"
                type="text"
                class="w-full border px-2 py-1 rounded"
              />
            </div>
            <!-- ถ้าไม่อยู่ในโหมดแก้ไข แสดงข้อมูล -->
            <div v-else>
              {{ reward.label }}
            </div>
          </td>
          <td class="px-4 py-2">
            <div v-if="editingRewardId === reward._id">
              <input
                v-model.number="editingRewardAmount"
                type="number"
                class="w-full border px-2 py-1 rounded"
              />
            </div>
            <div v-else>
              {{ formatNumber(reward.amount) }}
            </div>
          </td>
          <td class="px-4 py-2">
            <!-- ถ้าอยู่ในโหมดแก้ไข ให้แสดงปุ่มบันทึกและยกเลิก -->
            <div v-if="editingRewardId === reward._id" class="space-x-2">
              <button
                @click="updateReward"
                class="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
              >
                บันทึก
              </button>
              <button
                @click="cancelEditReward"
                class="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
              >
                ยกเลิก
              </button>
            </div>
            <!-- ถ้าไม่อยู่ในโหมดแก้ไข ให้แสดงปุ่มแก้ไขและลบ -->
            <div v-else class="space-x-2">
              <button
                @click="startEditReward(reward)"
                class="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
              >
                แก้ไข
              </button>
              <button
                @click="deleteReward(reward._id)"
                class="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
              >
                ลบ
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { API_PATHS } from '@/utils/ApiPath.js';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// สเตตสำหรับรายการรางวัล
const rewards = ref([]);

// สเตตสำหรับฟอร์มเพิ่มรางวัลใหม่
const newRewardLabel = ref('');
const newRewardAmount = ref(0);

// สเตตสำหรับแก้ไขรางวัล
const editingRewardId = ref(null);
const editingRewardLabel = ref('');
const editingRewardAmount = ref(0);

// ฟังก์ชันสำหรับใส่ comma คั่นหลักตัวเลข
const formatNumber = (value) => {
  if (!value || isNaN(value)) return '0';
  return Number(value).toLocaleString('th-TH');
};

// ดึงข้อมูลรางวัลจาก API
const fetchRewards = async () => {
  try {
    const response = await axios.get(API_PATHS.GET_REWARD);
    rewards.value = response.data;
  } catch (error) {
    console.error('Error fetching rewards:', error);
    Swal.fire({
      icon: 'error',
      title: 'เกิดข้อผิดพลาดในการดึงข้อมูลรางวัล',
      text: error.message,
      confirmButtonText: 'ตกลง',
    });
  }
};

onMounted(() => {
  fetchRewards();
});

// เพิ่มรางวัลใหม่
const addReward = async () => {
  if (!newRewardLabel.value || newRewardAmount.value <= 0) {
    Swal.fire({
      icon: 'warning',
      title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
      confirmButtonText: 'ตกลง',
    });
    return;
  }

  try {
    const payload = {
      label: newRewardLabel.value,
      amount: newRewardAmount.value,
    };
    const response = await axios.post(API_PATHS.CREATE_REWARD, payload);
    rewards.value.push(response.data);
    newRewardLabel.value = '';
    newRewardAmount.value = 0;
    Swal.fire({
      icon: 'success',
      title: 'เพิ่มรางวัลใหม่สำเร็จ',
      showConfirmButton: false,
      timer: 1500,
    });
  } catch (error) {
    console.error('Error adding reward:', error);
    Swal.fire({
      icon: 'error',
      title: 'เกิดข้อผิดพลาดในการเพิ่มรางวัล',
      text: error.response?.data?.message || error.message,
      confirmButtonText: 'ตกลง',
    });
  }
};

// เริ่มแก้ไขรางวัล โดยกำหนดสเตตของรางวัลที่จะแก้ไข
const startEditReward = (reward) => {
  editingRewardId.value = reward._id;
  editingRewardLabel.value = reward.label;
  editingRewardAmount.value = reward.amount;
};

// ยกเลิกการแก้ไข
const cancelEditReward = () => {
  editingRewardId.value = null;
  editingRewardLabel.value = '';
  editingRewardAmount.value = 0;
};

// อัปเดตรายการรางวัล
const updateReward = async () => {
  try {
    const payload = {
      label: editingRewardLabel.value,
      amount: editingRewardAmount.value,
    };
    const response = await axios.put(API_PATHS.EDIT_REWARD(editingRewardId.value), payload);
    const index = rewards.value.findIndex((r) => r._id === editingRewardId.value);
    if (index !== -1) {
      rewards.value[index] = response.data;
    }
    cancelEditReward();
    Swal.fire({
      icon: 'success',
      title: 'แก้ไขรางวัลสำเร็จ',
      showConfirmButton: false,
      timer: 1500,
    });
  } catch (error) {
    console.error('Error updating reward:', error);
    Swal.fire({
      icon: 'error',
      title: 'เกิดข้อผิดพลาดในการแก้ไขรางวัล',
      text: error.response?.data?.message || error.message,
      confirmButtonText: 'ตกลง',
    });
  }
};

// ลบรางวัล
const deleteReward = async (id) => {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'คุณต้องการลบรางวัลนี้หรือไม่?',
    showCancelButton: true,
    confirmButtonText: 'ใช่',
    cancelButtonText: 'ไม่ใช่',
  });

  if (result.isConfirmed) {
    try {
      await axios.delete(API_PATHS.DELETE_REWARD(id));
      rewards.value = rewards.value.filter((r) => r._id !== id);
      Swal.fire({
        icon: 'success',
        title: 'ลบรางวัลสำเร็จ',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error('Error deleting reward:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาดในการลบรางวัล',
        text: error.response?.data?.message || error.message,
        confirmButtonText: 'ตกลง',
      });
    }
  }
};
</script>
